import type { MissionMetric } from './metrics'

export const DAILY_MISSION_COUNT = 10
export const MISSION_PACK_THRESHOLD = 7
export const MISSION_COIN_REWARD = 3

/** 1 is a single duel, 2 is a short session, 3 spans several wins. */
export type MissionTier = 1 | 2 | 3

/**
 * Slots per category, in tier order. Totals 3 easy, 4 medium, 3 hard so the
 * shop pack (7 of 10) lands on the easy and medium rows.
 */
export const MISSION_TIER_QUOTA = {
  presence: [1, 2, 2, 3],
  skill: [1, 2, 3],
  flavor: [1, 2, 3],
} as const satisfies Record<string, readonly MissionTier[]>

export type MissionCategory = keyof typeof MISSION_TIER_QUOTA

export type MissionId =
  | 'play-1'
  | 'play-ai-1'
  | 'open-pack-1'
  | 'draw-15'
  | 'play-3'
  | 'play-ai-3'
  | 'xp-40'
  | 'draw-40'
  | 'open-pack-2'
  | 'play-5'
  | 'xp-90'
  | 'draw-80'
  | 'open-pack-5'
  | 'win-1'
  | 'win-lp-1'
  | 'survive-2000-1'
  | 'win-2'
  | 'win-ai-hard-1'
  | 'win-fast-1'
  | 'survive-2000-2'
  | 'win-lp-2'
  | 'win-4'
  | 'win-ai-hard-3'
  | 'win-fast-2'
  | 'survive-2000-4'
  | 'win-lp-4'
  | 'summon-8'
  | 'attack-10'
  | 'spell-5'
  | 'damage-2000'
  | 'summon-20'
  | 'attack-25'
  | 'spell-15'
  | 'trap-6'
  | 'tribute-5'
  | 'fusion-1'
  | 'damage-3500'
  | 'summon-40'
  | 'attack-45'
  | 'spell-28'
  | 'trap-12'
  | 'tribute-10'
  | 'fusion-3'
  | 'damage-5000'

export type MissionDef = {
  id: MissionId
  category: MissionCategory
  tier: MissionTier
  metric: MissionMetric
  target: number
  /** `max` keeps the highest value seen (battle damage in one turn). */
  kind: 'sum' | 'max'
}

export const MISSION_POOL: readonly MissionDef[] = [
  { id: 'play-1', category: 'presence', tier: 1, metric: 'played', target: 1, kind: 'sum' },
  { id: 'play-ai-1', category: 'presence', tier: 1, metric: 'playedAi', target: 1, kind: 'sum' },
  { id: 'open-pack-1', category: 'presence', tier: 1, metric: 'packsOpened', target: 1, kind: 'sum' },
  { id: 'draw-15', category: 'presence', tier: 1, metric: 'cardsDrawn', target: 15, kind: 'sum' },
  { id: 'play-3', category: 'presence', tier: 2, metric: 'played', target: 3, kind: 'sum' },
  { id: 'play-ai-3', category: 'presence', tier: 2, metric: 'playedAi', target: 3, kind: 'sum' },
  { id: 'xp-40', category: 'presence', tier: 2, metric: 'xp', target: 40, kind: 'sum' },
  { id: 'draw-40', category: 'presence', tier: 2, metric: 'cardsDrawn', target: 40, kind: 'sum' },
  { id: 'open-pack-2', category: 'presence', tier: 2, metric: 'packsOpened', target: 2, kind: 'sum' },
  { id: 'play-5', category: 'presence', tier: 3, metric: 'played', target: 5, kind: 'sum' },
  { id: 'xp-90', category: 'presence', tier: 3, metric: 'xp', target: 90, kind: 'sum' },
  { id: 'draw-80', category: 'presence', tier: 3, metric: 'cardsDrawn', target: 80, kind: 'sum' },
  { id: 'open-pack-5', category: 'presence', tier: 3, metric: 'packsOpened', target: 5, kind: 'sum' },
  { id: 'win-1', category: 'skill', tier: 1, metric: 'won', target: 1, kind: 'sum' },
  { id: 'win-lp-1', category: 'skill', tier: 1, metric: 'wonLp', target: 1, kind: 'sum' },
  { id: 'survive-2000-1', category: 'skill', tier: 1, metric: 'survive2000', target: 1, kind: 'sum' },
  { id: 'win-2', category: 'skill', tier: 2, metric: 'won', target: 2, kind: 'sum' },
  { id: 'win-ai-hard-1', category: 'skill', tier: 2, metric: 'wonAiHard', target: 1, kind: 'sum' },
  { id: 'win-fast-1', category: 'skill', tier: 2, metric: 'winFast', target: 1, kind: 'sum' },
  { id: 'survive-2000-2', category: 'skill', tier: 2, metric: 'survive2000', target: 2, kind: 'sum' },
  { id: 'win-lp-2', category: 'skill', tier: 2, metric: 'wonLp', target: 2, kind: 'sum' },
  { id: 'win-4', category: 'skill', tier: 3, metric: 'won', target: 4, kind: 'sum' },
  { id: 'win-ai-hard-3', category: 'skill', tier: 3, metric: 'wonAiHard', target: 3, kind: 'sum' },
  { id: 'win-fast-2', category: 'skill', tier: 3, metric: 'winFast', target: 2, kind: 'sum' },
  { id: 'survive-2000-4', category: 'skill', tier: 3, metric: 'survive2000', target: 4, kind: 'sum' },
  { id: 'win-lp-4', category: 'skill', tier: 3, metric: 'wonLp', target: 4, kind: 'sum' },
  { id: 'summon-8', category: 'flavor', tier: 1, metric: 'summons', target: 8, kind: 'sum' },
  { id: 'attack-10', category: 'flavor', tier: 1, metric: 'attacks', target: 10, kind: 'sum' },
  { id: 'spell-5', category: 'flavor', tier: 1, metric: 'spells', target: 5, kind: 'sum' },
  { id: 'damage-2000', category: 'flavor', tier: 1, metric: 'maxBattleDamageTurn', target: 2000, kind: 'max' },
  { id: 'summon-20', category: 'flavor', tier: 2, metric: 'summons', target: 20, kind: 'sum' },
  { id: 'attack-25', category: 'flavor', tier: 2, metric: 'attacks', target: 25, kind: 'sum' },
  { id: 'spell-15', category: 'flavor', tier: 2, metric: 'spells', target: 15, kind: 'sum' },
  { id: 'trap-6', category: 'flavor', tier: 2, metric: 'traps', target: 6, kind: 'sum' },
  { id: 'tribute-5', category: 'flavor', tier: 2, metric: 'tributes', target: 5, kind: 'sum' },
  { id: 'fusion-1', category: 'flavor', tier: 2, metric: 'fusions', target: 1, kind: 'sum' },
  { id: 'damage-3500', category: 'flavor', tier: 2, metric: 'maxBattleDamageTurn', target: 3500, kind: 'max' },
  { id: 'summon-40', category: 'flavor', tier: 3, metric: 'summons', target: 40, kind: 'sum' },
  { id: 'attack-45', category: 'flavor', tier: 3, metric: 'attacks', target: 45, kind: 'sum' },
  { id: 'spell-28', category: 'flavor', tier: 3, metric: 'spells', target: 28, kind: 'sum' },
  { id: 'trap-12', category: 'flavor', tier: 3, metric: 'traps', target: 12, kind: 'sum' },
  { id: 'tribute-10', category: 'flavor', tier: 3, metric: 'tributes', target: 10, kind: 'sum' },
  { id: 'fusion-3', category: 'flavor', tier: 3, metric: 'fusions', target: 3, kind: 'sum' },
  { id: 'damage-5000', category: 'flavor', tier: 3, metric: 'maxBattleDamageTurn', target: 5000, kind: 'max' },
]

const BY_ID = new Map(MISSION_POOL.map((def) => [def.id, def]))

export function missionDef(id: string): MissionDef | null {
  return BY_ID.get(id as MissionId) ?? null
}

export function isMissionId(value: unknown): value is MissionId {
  return typeof value === 'string' && BY_ID.has(value as MissionId)
}
